import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { api, API_BASE } from '../../api/client';
import ImageCropModal from '../../components/ImageCropModal';

interface Region { id: number; name: string; }
interface NewsItem {
  id: number;
  title: string;
  description: string;
  imageS3: string | null;
  createdAt: string;
  regionId: number | null;
}

export default function AdminNewsPage() {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);

  // Форма создания
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [language, setLanguage] = useState('ru');
  const [regionId, setRegionId] = useState<string>('');
  const [image, setImage] = useState<File | null>(null);
  const [pendingImage, setPendingImage] = useState<File | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // Редактирование
  const [editItem, setEditItem] = useState<NewsItem | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editLanguage, setEditLanguage] = useState('ru');
  const [editRegionId, setEditRegionId] = useState<string>('');
  const [editImage, setEditImage] = useState<File | null>(null);
  const [pendingEditImage, setPendingEditImage] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [newsRes, regRes] = await Promise.all([
      axios.get(`${API_BASE}/news`),
      axios.get(`${API_BASE}/regions`),
    ]);
    setNews(newsRes.data);
    setRegions(regRes.data);
  };

  useEffect(() => { load(); }, []);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    if (f) setPendingImage(f);
    e.target.value = '';
  };

  const handleEditImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    if (f) setPendingEditImage(f);
    e.target.value = '';
  };

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      const formData = new FormData();
      const data = JSON.stringify({ title, description, language, regionId: regionId ? Number(regionId) : null });
      formData.append('data', new Blob([data], { type: 'application/json' }));
      if (image) formData.append('image', image);

      await api.post('/news', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setTitle('');
      setDescription('');
      setLanguage('ru');
      setRegionId('');
      setImage(null);
      setPendingImage(null);
      await load();
    } catch {
      setError('Ошибка при создании новости');
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Удалить новость?')) return;
    await api.delete(`/news/${id}`);
    await load();
  };

  const openEdit = (item: NewsItem) => {
    setEditItem(item);
    setEditTitle(item.title);
    setEditDescription(item.description);
    setEditLanguage((item as any).language || 'ru');
    setEditRegionId(item.regionId ? String(item.regionId) : '');
    setEditImage(null);
    setPendingEditImage(null);
  };

  const saveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editItem) return;
    setSaving(true);
    try {
      const formData = new FormData();
      const data = JSON.stringify({ title: editTitle, description: editDescription, language: editLanguage, regionId: editRegionId ? Number(editRegionId) : null });
      formData.append('data', new Blob([data], { type: 'application/json' }));
      if (editImage) formData.append('image', editImage);
      await api.put(`/news/${editItem.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setEditItem(null);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const regionName = (id: number | null) =>
    regions.find(r => r.id === id)?.name || '—';

  return (
    <div style={styles.page}>
      <h2 style={styles.pageTitle}>Управление новостями</h2>

      {/* ── Создать новость ── */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>➕ Создать новость</h3>
        <form onSubmit={create} style={styles.form}>
          {error && <div style={styles.error}>{error}</div>}

          <input
            style={styles.input}
            placeholder="Заголовок новости"
            value={title}
            onChange={e => setTitle(e.target.value)}
            required
          />

          <textarea
            style={{ ...styles.input, resize: 'vertical' }}
            placeholder="Текст новости"
            rows={4}
            value={description}
            onChange={e => setDescription(e.target.value)}
            required
          />

          <select
            style={styles.input}
            value={language}
            onChange={e => setLanguage(e.target.value)}
          >
            <option value="ru">🇷🇺 Русский</option>
            <option value="ky">🇰🇬 Кыргызский</option>
            <option value="en">🇬🇧 English</option>
          </select>

          <select
            style={styles.input}
            value={regionId}
            onChange={e => setRegionId(e.target.value)}
          >
            <option value="">— Без региона (общая новость) —</option>
            {regions.map(r => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>

          <div>
            <label style={styles.fileLabel}>
              📎 Прикрепить изображение
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageChange}
              />
            </label>
            {image && <span style={styles.fileName}>{image.name}</span>}
          </div>

          <button style={styles.btnPrimary} type="submit" disabled={creating}>
            {creating ? 'Создание...' : 'Опубликовать'}
          </button>
        </form>
      </div>

      {/* ── Список новостей ── */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Все новости ({news.length})</h3>
        {news.length === 0 ? (
          <p style={styles.empty}>Новостей пока нет</p>
        ) : (
          news.map(item => (
            <div key={item.id} style={styles.newsRow}>
              {item.imageS3 && (
                <img
                  src={`${API_BASE}/news/media/${item.imageS3}`}
                  alt=""
                  style={styles.thumb}
                />
              )}
              <div style={styles.newsInfo}>
                <div style={styles.newsMeta}>
                  <span style={styles.newsRegion}>📍 {regionName(item.regionId)}</span>
                  <span style={styles.newsDate}>
                    {item.createdAt ? new Date(item.createdAt).toLocaleDateString('ru-RU') : ''}
                  </span>
                </div>
                <div style={styles.newsTitle}>{item.title}</div>
                <div style={styles.newsDesc}>
                  {item.description?.slice(0, 100)}{item.description?.length > 100 ? '...' : ''}
                </div>
              </div>
              <button onClick={() => openEdit(item)} style={styles.btnEdit}>✏️</button>
              <button onClick={() => remove(item.id)} style={styles.btnDanger}>🗑</button>
            </div>
          ))
        )}
      </div>
      {/* ── Модал редактирования ── */}
      {editItem && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>✏️ Редактировать новость</h3>
            <form onSubmit={saveEdit} style={styles.form}>
              <input
                style={styles.input}
                placeholder="Заголовок"
                value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                required
              />
              <textarea
                style={{ ...styles.input, resize: 'vertical' }}
                placeholder="Текст новости"
                rows={5}
                value={editDescription}
                onChange={e => setEditDescription(e.target.value)}
                required
              />
              <select
                style={styles.input}
                value={editLanguage}
                onChange={e => setEditLanguage(e.target.value)}
              >
                <option value="ru">🇷🇺 Русский</option>
                <option value="ky">🇰🇬 Кыргызский</option>
                <option value="en">🇬🇧 English</option>
              </select>
              <select
                style={styles.input}
                value={editRegionId}
                onChange={e => setEditRegionId(e.target.value)}
              >
                <option value="">— Без региона —</option>
                {regions.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
              <div>
                <label style={styles.fileLabel}>
                  📎 Заменить изображение (необязательно)
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleEditImageChange} />
                </label>
                {editImage && <span style={styles.fileName}>{editImage.name}</span>}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button style={styles.btnPrimary} type="submit" disabled={saving}>
                  {saving ? 'Сохранение...' : '💾 Сохранить'}
                </button>
                <button type="button" onClick={() => { setEditItem(null); setPendingEditImage(null); }} style={styles.btnCancel}>Отмена</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ImageCropModal
        file={pendingImage}
        aspect={16 / 9}
        title="Обрезать изображение новости"
        onCancel={() => setPendingImage(null)}
        onCropped={cropped => { setImage(cropped); setPendingImage(null); }}
      />
      <ImageCropModal
        file={pendingEditImage}
        aspect={16 / 9}
        title="Обрезать изображение новости"
        onCancel={() => setPendingEditImage(null)}
        onCropped={cropped => { setEditImage(cropped); setPendingEditImage(null); }}
      />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: 'clamp(16px, 4vw, 32px)', maxWidth: 900, margin: '0 auto' },
  pageTitle: { color: '#1a3a6b', fontSize: 'clamp(1.3rem, 3vw, 1.6rem)', marginBottom: 'clamp(16px, 3vw, 24px)' },
  card: { background: '#fff', borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: 16, overflow: 'hidden' },
  cardTitle: { color: '#1a3a6b', fontSize: 'clamp(0.9rem, 2vw, 1rem)', padding: '16px 20px', borderBottom: '1px solid #f0f4fb' },
  form: { display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 20px 20px' },
  input: { padding: '10px 14px', borderRadius: 8, border: '1px solid #dde3ef', fontSize: '0.95rem', outline: 'none', width: '100%' },
  fileLabel: {
    display: 'inline-block', padding: '8px 16px', background: '#f0f4fb',
    border: '1px dashed #1a3a6b', borderRadius: 8, cursor: 'pointer',
    fontSize: '0.88rem', color: '#1a3a6b', fontWeight: 600,
  },
  fileName: { marginLeft: 10, fontSize: '0.85rem', color: '#555' },
  btnPrimary: { padding: '10px 24px', background: '#1a3a6b', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start' },
  error: { background: '#fff0f0', color: '#c0392b', padding: '10px 14px', borderRadius: 8, fontSize: '0.9rem' },
  empty: { padding: '16px 20px', color: '#888', fontSize: '0.9rem' },
  newsRow: { display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 20px', borderTop: '1px solid #f0f4fb', flexWrap: 'wrap' },
  thumb: { width: 80, height: 60, objectFit: 'cover', borderRadius: 6, flexShrink: 0 },
  newsInfo: { flex: 1, minWidth: 200 },
  newsMeta: { display: 'flex', gap: 12, marginBottom: 4, flexWrap: 'wrap' },
  newsRegion: { fontSize: '0.78rem', color: '#1a3a6b', fontWeight: 600 },
  newsDate: { fontSize: '0.78rem', color: '#999' },
  newsTitle: { fontWeight: 600, color: '#1a2a4a', fontSize: '0.95rem', marginBottom: 4, wordBreak: 'break-word' },
  newsDesc: { fontSize: '0.85rem', color: '#666', wordBreak: 'break-word' },
  btnDanger: { padding: '6px 10px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', flexShrink: 0 },
  btnEdit: { padding: '6px 10px', background: '#f39c12', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', flexShrink: 0, marginRight: 6 },
  btnCancel: { padding: '10px 20px', background: '#eee', color: '#333', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal: { background: '#fff', borderRadius: 12, padding: 'clamp(20px, 4vw, 28px)', width: '100%', maxWidth: 560, boxShadow: '0 8px 32px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { color: '#1a3a6b', fontSize: 'clamp(1rem, 2vw, 1.1rem)', marginBottom: 16 },
};
